import React, { useEffect, useState } from 'react';
import { Button, Form, ListGroup, Modal, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { baseURL } from '../../../../helpers/baseURL';
import { headers } from '../../../../helpers/Requests';
import successAlert from '../../../../helpers/successAlert';
import failAlert from '../../../../helpers/failAlert';

const TransferTicketModal = ({ show, onHide, ticket, onTransferred }) => {
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [transferError, setTransferError] = useState('');

    useEffect(() => {
        if (!show) {
            setSelectedId(null);
            setTransferError('');
            return;
        }

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setTransferError('');
            try {
                const { data } = await axios.get(`${baseURL}ticket-transfer-targets`, headers);
                if (!cancelled) {
                    setWorkspaces(Array.isArray(data?.workspaces) ? data.workspaces : []);
                }
            } catch (error) {
                if (!cancelled) {
                    failAlert(error?.response?.data?.message ?? 'Failed to load workspaces.');
                    setWorkspaces([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [show]);

    const selectWorkspace = (ws) => {
        if (!ws?.can_transfer && ws?.can_transfer !== undefined) return;
        if (ws?.is_active === false) return;
        setSelectedId(ws.id);
        if (transferError) setTransferError('');
    };

    const canSelect = (ws) => ws?.can_transfer !== false && ws?.is_active !== false;

    const handleHide = () => {
        setTransferError('');
        onHide();
    };

    const submit = async () => {
        if (!selectedId || !ticket?.id) return;
        const selected = workspaces.find((ws) => ws.id === selectedId);
        if (selected && !canSelect(selected)) return;

        setSubmitting(true);
        setTransferError('');
        try {
            const { data } = await axios.post(
                `${baseURL}ticket/${ticket.id}/transfer`,
                { workspace_id: selectedId },
                headers
            );
            successAlert('Ticket transferred');
            handleHide();
            onTransferred?.(data);
        } catch (error) {
            const msg =
                error?.response?.data?.errors?.workspace_id?.[0] ||
                error?.response?.data?.errors?.ticket?.[0] ||
                error?.response?.data?.message ||
                'Transfer failed.';
            setTransferError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={handleHide} centered>
            <style>{`
                .transfer-workspace-radio .form-check-input:focus {
                    box-shadow: none;
                    outline: none;
                }
            `}</style>
            <Modal.Header closeButton>
                <Modal.Title>Transfer Ticket</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted small mb-3">
                    Transfer <strong>#{ticket?.ticket_id || ticket?.id}</strong> to a workspace you connected via Transfering Codes.
                </p>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" />
                    </div>
                ) : workspaces.length === 0 ? (
                    <div className="text-muted">
                        No workspaces available. Connect a transfer code from{' '}
                        <Link to="/settings/transfering-codes" onClick={handleHide}>
                            Transfering Codes
                        </Link>{' '}
                        under Settings to transfer tickets into that workspace.
                    </div>
                ) : (
                    <>
                        <ListGroup>
                            {workspaces.map((ws) => {
                                const enabled = canSelect(ws);
                                return (
                                    <ListGroup.Item
                                        key={ws.id}
                                        onClick={() => enabled && selectWorkspace(ws)}
                                        className="d-flex align-items-center"
                                        style={{
                                            cursor: enabled ? 'pointer' : 'not-allowed',
                                            opacity: enabled ? 1 : 0.65,
                                        }}
                                    >
                                        <Form.Check
                                            type="radio"
                                            id={`transfer-workspace-${ws.id}`}
                                            name="transfer-workspace"
                                            checked={selectedId === ws.id}
                                            disabled={!enabled}
                                            onChange={(e) => {
                                                if (!enabled) return;
                                                setSelectedId(ws.id);
                                                if (transferError) setTransferError('');
                                                e.target.blur();
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (enabled) e.currentTarget.blur();
                                            }}
                                            className="me-2 transfer-workspace-radio"
                                            style={{ cursor: enabled ? 'pointer' : 'not-allowed' }}
                                        />
                                        <label
                                            htmlFor={enabled ? `transfer-workspace-${ws.id}` : undefined}
                                            className="mb-0"
                                            style={{ cursor: enabled ? 'pointer' : 'not-allowed' }}
                                        >
                                            {ws.name}
                                            {!enabled ? (
                                                <span className="text-muted small ms-2">(transfer paused)</span>
                                            ) : null}
                                        </label>
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                        {transferError ? (
                            <div className="text-danger small mt-3" role="alert">
                                {transferError}
                            </div>
                        ) : null}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleHide} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={submit}
                    disabled={!selectedId || submitting || loading}
                >
                    {submitting ? 'Transferring…' : 'Transfer Ticket'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TransferTicketModal;
